import React, { useState, useRef, useEffect } from "react";

/**
 * EditableCell - Celda editable inline con validación y guardado.
 *
 * Props:
 * - value: number|string - Valor actual mostrado
 * - onSave: (newValue: number) => Promise<void>|void - Callback al guardar
 * - min: number (default: 0) - Valor mínimo permitido
 * - max: number (default: 99999) - Valor máximo permitido
 * - validate: (value: number) => string|null (optional) - Validación adicional, retorna mensaje de error o null
 * - disabled: boolean (optional) - Deshabilita la edición
 */
export function EditableCell({
  value,
  onSave,
  min = 0,
  max = 99999,
  validate,
  disabled = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Reset edit value when external value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value));
    }
  }, [value, isEditing]);

  const handleClick = () => {
    if (disabled || saving) return;
    setIsEditing(true);
    setEditValue(String(value));
    setError(null);
    setSuccess(false);
  };

  const validateValue = (val) => {
    const numVal = Number(val);
    if (val === "" || isNaN(numVal)) {
      return "Ingrese un valor numérico";
    }
    if (!Number.isInteger(numVal)) {
      return "Debe ser un valor entero";
    }
    if (numVal < min || numVal > max) {
      return `Valor debe estar entre ${min} y ${max.toLocaleString()}`;
    }
    if (validate) {
      return validate(numVal);
    }
    return null;
  };

  const handleBlur = async () => {
    const validationError = validateValue(editValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    const numericValue = Number(editValue);
    if (numericValue === Number(value)) {
      setIsEditing(false);
      setError(null);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(numericValue);
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
    if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(String(value));
      setError(null);
    }
  };

  if (isEditing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          type="number"
          min={min}
          max={max}
          step="1"
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            setError(null);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={saving}
          className={`w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 ${
            error
              ? "border-red-400 focus:ring-red-300"
              : "border-blue-400 focus:ring-blue-300"
          } ${saving ? "opacity-50" : ""}`}
        />
        {error && (
          <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 whitespace-nowrap z-10">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <span
      onClick={handleClick}
      className={`text-sm inline-flex items-center gap-1 ${
        disabled
          ? "text-gray-400 cursor-not-allowed"
          : "text-gray-700 cursor-pointer hover:bg-blue-50 hover:text-blue-700 rounded px-1 py-0.5 transition-colors"
      } ${success ? "text-green-600" : ""}`}
      title={disabled ? "" : "Clic para editar"}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleClick();
      }}
    >
      {value}
      {success && (
        <svg
          className="w-3 h-3 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )}
      {!disabled && !success && (
        <svg
          className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      )}
    </span>
  );
}

export default EditableCell;
